import * as R from "ramda";
import React from "react";
import { positionQueryParameter } from "../../actions/course-author/helpers";
import { LockMessageWindow } from "../../App/Common/Components/LockMessageWindow";
import CourseInstPreview from "../../App/CourseInstPreview";
import CustomModal, { ModalType } from "../../components/utils/CustomModal";
import { LastPartProvider } from "../../context/last-part";
import { RequestState } from "../../reducers/course-author/course-author";
import { courseInstructionPartViews, courseUnitViews, fullCourseViews } from "../../utils/courseViews";
import { PreferredLocale } from "../../utils/locales";
import { IPageLockSettings } from "../pages/CourseAuthor/CourseInstruction/Parts/PageLockSettings";
import CourseNavigation from "../pages/CourseAuthor/CourseNavigation";
import LocaleSelector from "../pages/CourseAuthor/LocaleSelector";
import Forbidden from "../pages/Forbidden";
import SaveInProgress from "../utils/SaveInProgress";
import { SideMenu } from "../utils/SideMenu/SideMenu.container";
import Spinner from "../utils/Spinner";
import SubTopbar from "../utils/SubTopbar";
import TopBar from "../utils/TopBar";
import { ICourseAuthorProps, ICourseAuthorState } from "./CourseAuthor.types";
import { CourseChanges } from "./helpers/patches";
import {
  addChoiceExerciseItemPatch,
  addClozeGapPatch,
  addComponentPatch,
  addComponentTitlePatch,
  addCourseDescriptionPatch,
  addCourseInstructionPartPatch,
  copyCourseInstructionPartPatch,
  addCourseTitlePatch,
  editDividerContentPatch,
  editImageContentPatch,
  addHTMLContentPatch,
  editSpacerContentPatch,
  addVoiceOverPatch,
  addVoiceOverTextPatch,
  changeInstructionTypePatch,
  copyComponentPatch,
  deleteClozeGapPatch,
  deleteComponentPatch,
  deleteCourseInstructionPartPatch,
  deleteElementPatch,
  deleteExerciseItemPatch,
  enableVoiceOverPatch,
  insertElementPatch,
  modifyAssessmentSettingPatch,
  modifyAssessmentShowCorrectPatch,
  modifyChoiceExerciseItemCorrectPatch,
  modifyChoiceExerciseItemTextPatch,
  modifyChoiceExerciseRandomPatch,
  modifyChoiceExerciseWrapperContentPatch,
  modifyClozeGapAnswersPatch,
  modifyClozeGapHintPatch,
  modifyClozeTemplatePatch,
  modifyComponentTitlePatch,
  modifyCourseContentSettingsPatch,
  modifyCourseDeletedPatch,
  modifyCourseDescriptionPatch,
  modifyCourseLinearityPatch,
  modifyCourseTitlePatch,
  modifyCustomCompletedMessagePatch,
  modifyExerciseItemHintPatch,
  modifyHTMLContentPatch,
  modifyMasterLocalePatch,
  modifyOrganizationOwner,
  modifyPageLockSettingsPatch,
  modifyUseCustomCompletedMessagePatch,
  modifyVoiceOverMediaPatch,
  modifyVoiceOverTextPatch,
  modifyVoiceOverTimePatch,
  moveComponentPatch,
  publicationPatch,
  republicationPatch,
  substLeafCourseInstructionColumnPatch,
  toggleLockCoursePatch,
  unpublicationPatch,
  changeBackgroundPatch,
  togglePseudoLessonPatch,
  createGridContentPatches,
  editDocumentContentPatch,
} from "./helpers/patches/creators";
import { findComponentPath } from "./helpers/patches/util";
import { SimplePreview } from "./SimplePreview";
import CourseTabs, { CourseTabsEnum } from "./tabs/CourseTabs";
import {
  BackgroundAblePart,
  ChoiceExerciseWrapping,
  CourseComponentTag,
  CourseComponentType,
  CourseContentType,
  CourseInstructionType,
  CourseUnitView,
  IAssessment,
  IChoiceExercise,
  IClozeExercise,
  ICourseComponent,
  ICourseContentSettings,
  ICourseEditOperations,
  ICourseInstruction,
  ICourseInstructionContentPart,
  ICourseNavigationOperations,
  ICoursePublication,
  IDividerPart,
  IDividerSettings,
  IDocumentPart,
  IDocumentSettings,
  IFullCourse,
  IGridPart,
  IImagePart,
  IImageSettings,
  IQuestionPart,
  ISpacerPart,
  ISpacerSettings,
  isUnit,
  ITeachingContent,
  QuestionDrawingPreference,
  Slide,
} from "./types";

const DEFAULT_LOCALE = PreferredLocale.en;
export class CourseAuthorComponent extends React.Component<ICourseAuthorProps, ICourseAuthorState>
  implements ICourseEditOperations, ICourseNavigationOperations {
  private editOperations: ICourseEditOperations;
  private navigationOperations: ICourseNavigationOperations;

  constructor(props: ICourseAuthorProps) {
    super(props);
    this.state = {
      currentLocale: fullCourseViews.masterLocale(props.course) || DEFAULT_LOCALE,
      displayedComponent: null,
      lastPart: null,
      menuExpanded: false,
      unitPath: [],
    };

    this.editOperations = {
      addChoiceExerciseItem: this.addChoiceExerciseItem,
      addClozeGap: this.addClozeGap,
      addCourseDescription: this.addCourseDescription,
      addImmersiveBackground: this.addImmersiveBackground,
      addPart: this.addPart,
      addTitle: this.addTitle,
      changeBackground: this.changeBackground,
      changeInstructionType: this.changeInstructionType,
      cloneCourseIntoOrg: this.cloneCourseIntoOrg,
      copyGridParts: this.copyGridParts,
      copyPart: this.copyPart,
      createComponent: this.createComponent,
      createStyle: this.createStyle,
      deleteClozeGap: this.deleteClozeGap,
      deleteComponent: this.deleteComponent,
      deleteCourse: this.deleteCourse,
      deleteElement: this.deleteElement,
      deleteExerciseItem: this.deleteExerciseItem,
      deleteInstructionPart: this.deleteInstructionPart,
      editDivider: this.editDivider,
      editDocument: this.editDocument,
      editHTMLContent: this.editHTMLContent,
      editImage: this.editImage,
      editSpacer: this.editSpacer,
      editVoiceOverMedia: this.editVoiceOverMedia,
      editVoiceOverText: this.editVoiceOverText,
      editVoiceOverTime: this.editVoiceOverTime,
      enableVoiceOverAudio: this.enableVoiceOverAudio,
      insertElement: this.insertElement,
      modifyAssessmentAccumulate: this.modifyAssessmentAccumulate,
      modifyAssessmentDrawingPreference: this.modifyAssessmentDrawingPreference,
      modifyAssessmentLimitMistakes: this.modifyAssessmentLimitMistakes,
      modifyAssessmentMistakesAllowed: this.modifyAssessmentMistakesAllowed,
      modifyAssessmentNumCorrectRequired: this.modifyAssessmentNumCorrectRequired,
      modifyAssessmentRandomized: this.modifyAssessmentRandomized,
      modifyAssessmentSettings: this.modifyAssessmentSettings,
      modifyAssessmentShowCorrect: this.modifyAssessmentShowCorrect,
      modifyChoiceExerciseItemCorrect: this.modifyChoiceExerciseItemCorrect,
      modifyChoiceExerciseRandom: this.modifyChoiceExerciseRandom,
      modifyChoiceExerciseWrapperContent: this.modifyChoiceExerciseWrapperContent,
      modifyClozeGapAnswers: this.modifyClozeGapAnswers,
      modifyClozeGapHint: this.modifyClozeGapHint,
      modifyClozeTemplate: this.modifyClozeTemplate,
      modifyCourseDescription: this.modifyCourseDescription,
      modifyCourseLinearity: this.modifyCourseLinearity,
      modifyCustomCompletedMessage: this.modifyCustomCompletedMessage,
      modifyExerciseItemHint: this.modifyExerciseItemHint,
      modifyExerciseItemText: this.modifyExerciseItemText,
      modifyMasterLocale: this.modifyMasterLocale,
      modifyOrganizationOwner: this.modifyOrganizationOwner,
      modifyPageLockSettings: this.modifyPageLockSettings,
      modifyTitle: this.modifyTitle,
      modifyUseCustomCompletedMessage: this.modifyUseCustomCompletedMessage,
      moveOrCopyComponent: this.moveOrCopyComponent,
      publishVersion: this.publishVersion,
      substColumnPart: this.substColumnPart,
      toggleLockCourse: this.toggleLockCourse,
      togglePseudoLesson: this.togglePseudoLesson,
      unpublishVersion: this.unpublishVersion,
    };

    this.navigationOperations = {
      setDisplayedComponent: this.setDisplayedComponent,
      setPath: this.setPath,
      truncatePath: this.truncatePath,
    };
    this.props.mount(this.props.match.params.courseId);
  }

  public componentWillUnmount() {
    this.props.unmount();
  }

  public componentDidUpdate(oldProps: ICourseAuthorProps) {
    const {
      course,
      location: { search },
    } = this.props;
    const position = positionQueryParameter(search);
    if (course === oldProps.course || !position) {
      return;
    }
    this.setDisplayedComponentAndPath(position);
  }

  public setPath = (unitPath: CourseUnitView[]) => this.setState({ unitPath });

  public truncatePath = (index: number) => {
    this.setState({
      unitPath: R.slice(0, index + 1, this.state.unitPath),
    });
  };

  public setDisplayedComponent = (displayedComponent: Slide) => () => this.setState({ displayedComponent });

  public clearLastPart = () => this.setState({ lastPart: null });

  public setLastPart = (value: number) => this.setState({ lastPart: value });

  public enableVoiceOverAudio = (path: CourseUnitView[], comp: ICourseInstruction) => (
    part: ICourseInstructionContentPart
  ) => () => {
    const { course, patchCourse, userId } = this.props;
    const currentVoiceOver = courseInstructionPartViews.localizedVoiceOver(part, this.state.currentLocale);
    const createPatchFun = R.isNil(currentVoiceOver) ? addVoiceOverPatch : enableVoiceOverPatch;

    const patch = createPatchFun(course, path, comp, part, this.state.currentLocale, userId);

    return patchCourse(course.versionId, patch);
  };

  public editVoiceOverMedia = (path: CourseUnitView[], comp: ICourseInstruction) => (
    part: ICourseInstructionContentPart
  ) => (mediaId: string) => {
    const { course, patchCourse, userId } = this.props;
    const modifyVOPatch = modifyVoiceOverMediaPatch(
      course,
      path,
      comp,
      part,
      this.state.currentLocale,
      mediaId,
      userId
    );

    return patchCourse(course.versionId, modifyVOPatch);
  };

  public editVoiceOverTime = (path: CourseUnitView[], comp: ICourseInstruction) => (
    part: ICourseInstructionContentPart
  ) => (startAt: Nullable<number>, stopAt: Nullable<number>) => {
    const { course, patchCourse, userId } = this.props;
    const modifyVOPatches = modifyVoiceOverTimePatch(
      course,
      path,
      comp,
      part,
      this.state.currentLocale,
      startAt,
      stopAt,
      userId
    );

    return patchCourse(course.versionId, modifyVOPatches);
  };

  public editVoiceOverText = (path: CourseUnitView[], comp: ICourseInstruction) => (part: ITeachingContent) => (
    text: string
  ) => {
    const { course, patchCourse, userId } = this.props;
    const currentVoiceOverText = courseInstructionPartViews.localizedVoiceOverText(part, this.state.currentLocale);
    const createPatchFn = R.isNil(currentVoiceOverText) ? addVoiceOverTextPatch : modifyVoiceOverTextPatch;

    const patch = createPatchFn(course, path, comp, part, this.state.currentLocale, text, userId);

    return patchCourse(course.versionId, patch);
  };

  public changeInstructionType = (path: CourseUnitView[], item: ICourseInstruction, type: CourseInstructionType) => {
    const { course, userId } = this.props;
    const patch = changeInstructionTypePatch(course, path, item, type, userId);
    this.props.patchCourse(course.versionId, patch);
  };

  public cloneCourseIntoOrg = (organizationId: string) => {
    this.props.cloneCourse(this.props.course, organizationId, this.props.backToCoursesPage);
  };

  public createComponent = (path: CourseUnitView[], tag: CourseComponentTag, type?: CourseInstructionType) => () => {
    const { course, userId } = this.props;
    const patch = addComponentPatch(course, path, userId, tag, type);

    if (!R.isNil(patch)) {
      this.props.patchCourse(course.versionId, patch);
    }
  };

  public addImmersiveBackground = () => {
    const { course } = this.props;
    if (R.isNil(fullCourseViews.style(course))) {
      this.props.createStyle();
    }
  };

  public createStyle = () => this.props.createStyle();

  public addChoiceExerciseItem = (unitPath: CourseUnitView[], slide: ICourseInstruction | IAssessment) => (
    part: ICourseInstructionContentPart
  ) => () => {
    const { course, userId } = this.props;
    const patch = addChoiceExerciseItemPatch(course, unitPath, slide, part, userId);
    this.props.patchCourse(course.versionId, patch);
  };

  public addClozeGap = (unitPath: CourseUnitView[], instructionSlide: ICourseInstruction | IAssessment) => (
    part: ICourseInstructionContentPart
  ) => (count: number) => {
    const { course, userId } = this.props;
    const patch = addClozeGapPatch(course, unitPath, instructionSlide, part, count, userId);
    this.props.patchCourse(course.versionId, patch);
  };

  public deleteExerciseItem = (unitPath: CourseUnitView[], slide: ICourseInstruction | IAssessment) => (
    part: ICourseInstructionContentPart
  ) => (choiceId: number) => () => {
    const { course, userId } = this.props;
    const patch = deleteExerciseItemPatch(course, unitPath || this.state.unitPath, slide, part.id, choiceId, userId);
    this.props.patchCourse(course.versionId, patch);
  };

  public deleteClozeGap = (unitPath: CourseUnitView[], instructionSlide: ICourseInstruction | IAssessment) => (
    part: ICourseInstructionContentPart
  ) => (gapId: number) => () => {
    const { course, userId } = this.props;
    const patch = deleteClozeGapPatch(course, unitPath, instructionSlide, part.id, gapId, userId);
    this.props.patchCourse(course.versionId, patch);
  };

  public modifyClozeGapAnswers = (unitPath: CourseUnitView[], instructionSlide: ICourseInstruction | IAssessment) => (
    part: ICourseInstructionContentPart
  ) => (
    gapId: number,
    answerId: number,
    oldAnswers: string[],
    newAnswers: string[],
    currentLocale: PreferredLocale
  ) => () => {
    const { course, userId } = this.props;
    const patch = modifyClozeGapAnswersPatch(
      course,
      unitPath,
      instructionSlide,
      part,
      gapId,
      answerId,
      oldAnswers,
      newAnswers,
      currentLocale,
      userId
    );
    this.props.patchCourse(course.versionId, patch);
  };

  public modifyClozeGapHint = (unitPath: CourseUnitView[], instructionSlide: ICourseInstruction | IAssessment) => (
    part: ICourseInstructionContentPart
  ) => (gapId: number, hintId: number, oldHint: string, newHint: string, currentLocale: PreferredLocale) => () => {
    const { course, userId } = this.props;
    const patch = modifyClozeGapHintPatch(
      course,
      unitPath,
      instructionSlide,
      part,
      gapId,
      hintId,
      oldHint,
      newHint,
      currentLocale,
      userId
    );
    this.props.patchCourse(course.versionId, patch);
  };

  public deleteComponent = (path: CourseUnitView[], component: ICourseComponent) => () => {
    const { course, userId } = this.props;
    const patch = deleteComponentPatch(course, path || this.state.unitPath, component, userId);
    this.props.patchCourse(course.versionId, patch);
  };

  public deleteCourse = () => {
    const { course, patchCourse, userId, reduceCourseAmount } = this.props;
    const patch = modifyCourseDeletedPatch(course, userId);
    patchCourse(course.versionId, patch);
    reduceCourseAmount();
  };

  public modifyAssessmentAccumulate = (path: CourseUnitView[], accumulate: boolean) => {
    const { course, patchCourse, userId } = this.props;
    const patch = modifyAssessmentSettingPatch(course, path, userId, "accumulateAnswers", accumulate);
    patchCourse(course.versionId, patch);
  };

  public modifyAssessmentDrawingPreference = (path: CourseUnitView[], preference: QuestionDrawingPreference) => {
    const { course, patchCourse, userId } = this.props;
    const patch = modifyAssessmentSettingPatch(course, path, userId, "questionDrawingPreference", preference);
    patchCourse(course.versionId, patch);
  };

  public modifyAssessmentLimitMistakes = (path: CourseUnitView[], shouldLimit: boolean) => {
    const { course, userId, patchCourse } = this.props;
    const patch = modifyAssessmentSettingPatch(course, path, userId, "limitAllowedMistakes", shouldLimit);
    patchCourse(course.versionId, patch);
  };

  public modifyAssessmentMistakesAllowed = (path: CourseUnitView[], num: number) => {
    const { course, userId, patchCourse } = this.props;
    const assessment = fullCourseViews.assessment(course, path);

    if (assessment && !assessment.limitAllowedMistakes) {
      const limitMistakesPatch = modifyAssessmentSettingPatch(course, path, userId, "limitAllowedMistakes", true);
      const setLimitPatch = modifyAssessmentSettingPatch(
        {
          ...course,
          versionId: course.versionId + 1,
        },
        path,
        userId,
        "numAllowedMistakes",
        num
      );
      return patchCourse(course.versionId, [limitMistakesPatch, setLimitPatch]);
    }

    const patch = modifyAssessmentSettingPatch(course, path, userId, "numAllowedMistakes", num);
    return patchCourse(course.versionId, patch);
  };

  public modifyAssessmentNumCorrectRequired = (path: CourseUnitView[], num: number) => {
    const { course, patchCourse, userId } = this.props;
    const patch = modifyAssessmentSettingPatch(course, path, userId, "numCorrectAnswersRequired", num);
    patchCourse(course.versionId, patch);
  };

  public modifyAssessmentRandomized = (path: CourseUnitView[], random: boolean) => {
    const { course, patchCourse, userId } = this.props;
    const patch = modifyAssessmentSettingPatch(course, path, userId, "isRandomized", random);
    patchCourse(course.versionId, patch);
  };

  public modifyAssessmentSettings = (path: CourseUnitView[], settings: ICourseContentSettings) => {
    const { course, patchCourse, userId } = this.props;
    const patches = modifyCourseContentSettingsPatch(course, path, settings, userId);
    patchCourse(course.versionId, patches);
  };

  public modifyAssessmentShowCorrect = (path: CourseUnitView[], showCorrect: boolean) => {
    const { course, patchCourse, userId } = this.props;
    const patches = modifyAssessmentShowCorrectPatch(course, path, showCorrect, userId);
    patchCourse(course.versionId, patches);
  };

  public modifyTitle = (path: CourseUnitView[], component?: ICourseComponent) => (title: string) => {
    const { course, patchCourse, userId } = this.props;

    const patch = R.isNil(component)
      ? modifyCourseTitlePatch(course, userId, this.state.currentLocale, title)
      : modifyComponentTitlePatch(
          course,
          path || this.state.unitPath,
          component,
          userId,
          this.state.currentLocale,
          title
        );
    patchCourse(course.versionId, patch);
  };

  public togglePseudoLesson = (path: CourseUnitView[], instruction: ICourseInstruction) => (checked: boolean) => {
    const { course, userId, patchCourse } = this.props;
    const patch = togglePseudoLessonPatch(userId, course.versionId, path, course, instruction, checked);

    patchCourse(course.versionId, patch);
  };

  public modifyUseCustomCompletedMessage = () => {
    const { course, patchCourse, userId } = this.props;
    const patch = modifyUseCustomCompletedMessagePatch(course, userId);
    patchCourse(course.versionId, patch);
  };

  public addCourseDescription = (description: string) => {
    const { course, patchCourse, userId } = this.props;
    const patch = addCourseDescriptionPatch(course, this.state.currentLocale, description, userId);

    patchCourse(course.versionId, patch);
  };

  public editHTMLContent = (path: CourseUnitView[], comp: ICourseInstruction) => (part: ITeachingContent) => (
    content: string,
    currentLocale?: PreferredLocale
  ) => {
    const { course, patchCourse, userId } = this.props;
    const currentContent = courseInstructionPartViews.localizedContent(part, currentLocale || this.state.currentLocale);
    const createPatchFn = R.isNil(currentContent) ? addHTMLContentPatch : modifyHTMLContentPatch;

    const patch = createPatchFn(course, path, comp, part, currentLocale || this.state.currentLocale, content, userId);

    return patchCourse(course.versionId, patch);
  };

  public changeBackground = (path: CourseUnitView[], comp: ICourseInstruction) => (part: BackgroundAblePart) => (
    imageId: string
  ) => {
    const { course, patchCourse, userId } = this.props;
    const patch = changeBackgroundPatch(course, path, comp, part, imageId, userId);

    return patchCourse(course.versionId, patch);
  };

  public modifyChoiceExerciseItemCorrect = (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (part: IChoiceExercise) => (choiceId: number) => () => {
    const { course, userId } = this.props;
    const patch = modifyChoiceExerciseItemCorrectPatch(
      course,
      unitPath || this.state.unitPath,
      instructionSlide,
      part,
      choiceId,
      userId
    );
    this.props.patchCourse(course.versionId, patch);
  };

  public modifyExerciseItemHint = (unitPath: CourseUnitView[], instructionSlide: ICourseInstruction | IAssessment) => (
    part: IChoiceExercise
  ) => (choiceId: number) => (content: string, currentLocale?: PreferredLocale) => {
    const { course, userId } = this.props;
    const patch = modifyExerciseItemHintPatch(
      course,
      currentLocale || this.state.currentLocale,
      unitPath || this.state.unitPath,
      instructionSlide,
      part,
      choiceId,
      content,
      userId
    );
    this.props.patchCourse(course.versionId, patch);
  };

  public modifyExerciseItemText = (unitPath: CourseUnitView[], instructionSlide: ICourseInstruction | IAssessment) => (
    part: IQuestionPart
  ) => (itemId: number) => (content: string, currentLocale?: PreferredLocale) => {
    const { course, userId } = this.props;
    const patch = modifyChoiceExerciseItemTextPatch(
      course,
      currentLocale || this.state.currentLocale,
      unitPath || this.state.unitPath,
      instructionSlide,
      part,
      itemId,
      content,
      userId
    );
    this.props.patchCourse(course.versionId, patch);
  };

  public modifyChoiceExerciseWrapperContent = (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (part: IChoiceExercise) => (
    text: string,
    wrapperKey: ChoiceExerciseWrapping,
    currentLocale?: PreferredLocale
  ) => {
    const { course, userId } = this.props;
    const patch = modifyChoiceExerciseWrapperContentPatch(
      wrapperKey,
      course,
      currentLocale || this.state.currentLocale,
      unitPath || this.state.unitPath,
      instructionSlide,
      part,
      text,
      userId
    );
    this.props.patchCourse(course.versionId, patch);
  };

  public modifyChoiceExerciseRandom = (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (part: IChoiceExercise) => () => {
    const { course, userId } = this.props;
    const patch = modifyChoiceExerciseRandomPatch(
      course,
      unitPath || this.state.unitPath,
      instructionSlide,
      part,
      userId
    );
    this.props.patchCourse(course.versionId, patch);
  };

  public modifyClozeTemplate = (unitPath: CourseUnitView[], instructionSlide: ICourseInstruction | IAssessment) => (
    part: ICourseInstructionContentPart
  ) => (template: string, currentLocale: PreferredLocale) => {
    const { course, userId } = this.props;
    const patch = modifyClozeTemplatePatch(
      course,
      unitPath,
      instructionSlide,
      part as IClozeExercise,
      template,
      currentLocale,
      userId
    );
    this.props.patchCourse(course.versionId, patch);
  };

  public modifyCourseDescription = (description: string) => {
    const { course, patchCourse, userId } = this.props;
    const patch = modifyCourseDescriptionPatch(course, this.state.currentLocale, description, userId);

    patchCourse(course.versionId, patch);
  };

  public modifyCourseLinearity = () => {
    const { course, patchCourse, userId } = this.props;
    const patch = modifyCourseLinearityPatch(course, userId);
    patchCourse(course.versionId, patch);
  };

  public modifyCustomCompletedMessage = (message: string) => {
    const { course, patchCourse, userId } = this.props;
    const patch = modifyCustomCompletedMessagePatch(course, message, this.state.currentLocale, userId);
    patchCourse(course.versionId, patch);
  };

  public modifyMasterLocale = (locale: PreferredLocale) => {
    const { course, patchCourse, userId } = this.props;
    const patch = modifyMasterLocalePatch(course, locale, userId);
    patchCourse(course.versionId, patch);
  };

  public insertElement = <T extends any>(element: T, position: string) => {
    const { course, patchCourse, userId } = this.props;
    const patch = insertElementPatch(course, userId, element, position);
    patchCourse(course.versionId, patch);
  };

  public deleteElement = <T extends any>(element: T, position: string) => {
    const { course, patchCourse, userId } = this.props;
    const patch = deleteElementPatch(course, userId, element, position);
    patchCourse(course.versionId, patch);
  };

  public deleteInstructionPart = (unitPath: CourseUnitView[], instructionSlide: ICourseInstruction | IAssessment) => (
    part: ICourseInstructionContentPart
  ) => () => {
    const { course, patchCourse, userId } = this.props;
    const patch = deleteCourseInstructionPartPatch(course, userId, instructionSlide, unitPath, part);
    return patchCourse(course.versionId, patch);
  };

  public modifyPageLockSettings = (path: CourseUnitView[], component: ICourseInstruction) => (
    settings: IPageLockSettings
  ) => {
    const { course, patchCourse, userId } = this.props;
    const patch = modifyPageLockSettingsPatch(course, userId, component, path, settings);
    return patchCourse(course.versionId, patch);
  };

  public addPart = (path: CourseUnitView[], comp: ICourseInstruction) => (
    type: CourseComponentType,
    position: number,
    properties?: object
  ) => {
    const { course, patchCourse, userId } = this.props;
    const patch = addCourseInstructionPartPatch(course, userId, comp, path, type, position, properties);
    this.setLastPart(patch.patch.action.item.id);
    return patchCourse(course.versionId, patch);
  };

  public copyGridParts = (path: CourseUnitView[], comp: ICourseInstruction) => (
    gridPart: IGridPart,
    childParts: Array<ICourseInstructionContentPart | null>,
    position: number | undefined
  ) => {
    const { course, userId, patchCourse } = this.props;
    const gridPatch = copyCourseInstructionPartPatch(course, userId, comp, path, gridPart, position);
    const gridContentPatches = createGridContentPatches(course, userId, comp, path, gridPatch, childParts, position);
    return patchCourse(course.versionId, [gridPatch, ...gridContentPatches]);
  };

  public copyPart = (path: CourseUnitView[], comp: ICourseInstruction) => (
    part: ICourseInstructionContentPart,
    position: number | undefined
  ) => {
    const { course, patchCourse, userId } = this.props;
    const patch = copyCourseInstructionPartPatch(course, userId, comp, path, part, position);
    this.setLastPart(patch.patch.action.item.id);
    return patchCourse(course.versionId, patch);
  };

  public substColumnPart = (path: CourseUnitView[], comp: ICourseInstruction) => (part: IGridPart) => (
    columnIndex: number,
    newColumnValue: {
      type: CourseComponentType;
      properties?: object;
    } | null
  ) => {
    const { course, patchCourse, userId } = this.props;

    if (R.isNil(newColumnValue)) {
      return;
    }

    // NOTE: The position is undefined so that it always adds the new part
    // at the end of the list.
    const undefinedPosition = undefined;
    const { type, properties } = newColumnValue;
    const addPatch = addCourseInstructionPartPatch(course, userId, comp, path, type, undefinedPosition, properties);
    const substPatch = substLeafCourseInstructionColumnPatch(
      course,
      userId,
      comp,
      path,
      part,
      columnIndex,
      addPatch.patch.action.at
    );
    return patchCourse(course.versionId, [addPatch, substPatch]);
  };

  public addTitle = (path?: CourseUnitView[], component?: ICourseComponent) => (title: string) => {
    const { course, patchCourse, userId } = this.props;

    if (R.isNil(component)) {
      const courseTitlePatch = addCourseTitlePatch(course, userId, this.state.currentLocale, title);
      return patchCourse(course.versionId, courseTitlePatch);
    }

    const componentTitlePatch = addComponentTitlePatch(
      course,
      path || this.state.unitPath,
      component,
      userId,
      this.state.currentLocale,
      title
    );
    patchCourse(course.versionId, componentTitlePatch);
  };

  public moveOrCopyComponent = (path: CourseUnitView[], component: ICourseComponent, dropPosition: number) => {
    const { course, patchCourse, userId } = this.props;
    const destinationComponentList = this.courseComponentListAt(path);

    if (R.contains(component, destinationComponentList)) {
      const currentIndex: number = R.indexOf(component, destinationComponentList);
      const dropPositionAux = dropPosition > currentIndex ? dropPosition - 1 : dropPosition;
      const insertAt: number = Math.max(0, Math.min(dropPositionAux, destinationComponentList.length - 1));

      const movePatch = moveComponentPatch(course, path, component, userId, insertAt);
      return patchCourse(course.versionId, movePatch);
    }

    const copyPatch = copyComponentPatch(course, path, component, userId, dropPosition);
    patchCourse(course.versionId, copyPatch);
  };

  public editDivider = (path: CourseUnitView[], comp: ICourseInstruction) => (part: IDividerPart) => (
    value: IDividerSettings
  ) => {
    const { course, patchCourse, userId } = this.props;
    const patch = editDividerContentPatch(course, path, comp, part, value, userId);

    return patchCourse(course.versionId, patch);
  };

  public editSpacer = (path: CourseUnitView[], comp: ICourseInstruction) => (part: ISpacerPart) => (
    value: ISpacerSettings
  ) => {
    const { course, patchCourse, userId } = this.props;
    const patch = editSpacerContentPatch(course, path, comp, part, value, userId);

    return patchCourse(course.versionId, patch);
  };

  public editImage = (path: CourseUnitView[], comp: ICourseInstruction) => (part: IImagePart) => (
    value: IImageSettings
  ) => {
    const { course, patchCourse, userId } = this.props;
    const patch = editImageContentPatch(course, path, comp, part, value, userId);

    return patchCourse(course.versionId, patch);
  };

  public editDocument = (path: CourseUnitView[], comp: ICourseInstruction) => (part: IDocumentPart) => (
    value: IDocumentSettings
  ) => {
    const { course, patchCourse, userId } = this.props;
    const patch = editDocumentContentPatch(course, path, comp, part, value, userId);

    return patchCourse(course.versionId, patch);
  };

  public publishVersion = (versionId?: number) => {
    const { course, patchCourse, userId } = this.props;
    const publications = fullCourseViews.publications(course) || [];
    const publicationVersion = versionId || course.versionId;
    const previouslyPublished = R.any(p => p.revision === publicationVersion, publications);
    const targetPublication = R.find(p => p.revision === publicationVersion, publications);

    const patch =
      previouslyPublished && targetPublication
        ? republicationPatch(course, userId, targetPublication.id)
        : publicationPatch(course, userId, publicationVersion);
    patchCourse(course.versionId, patch);
  };

  public toggleLockCourse = () => {
    const { course, patchCourse, userId } = this.props;
    const patch = toggleLockCoursePatch(course, userId);
    patchCourse(course.versionId, patch);
  };

  public unpublishVersion = (versionId: number) => {
    const { course, patchCourse, userId } = this.props;
    const index = R.findIndex(p => p.revision === versionId, fullCourseViews.publications(course) || []);
    const patch = unpublicationPatch(course, userId, index);
    patchCourse(course.versionId, patch);
  };

  public modifyOrganizationOwner = (newOrganizationId: string) => {
    const { course, patchCourse, userId } = this.props;
    const patch = modifyOrganizationOwner(userId, course, newOrganizationId);
    patchCourse(course.versionId, patch);
  };

  public setLocale = (currentLocale: PreferredLocale) => this.setState({ currentLocale });

  public render() {
    const {
      authenticationState: { isAuthenticated, isKnown },
      course,
      isAdmin,
      organizationName,
      userName,
      fetchCourseRequestState,
      displayPreviewCourseInstFailMessage,
      multiLocale,
      backToCoursesPage,
    } = this.props;

    if (fullCourseViews.isDeleted(course)) {
      backToCoursesPage();
    }

    if (fetchCourseRequestState !== RequestState.SUCCEEDED || !isKnown) {
      return <Spinner />;
    }

    if (!isAuthenticated || !isAdmin) {
      return <Forbidden />;
    }

    const hasLocale: boolean = multiLocale && course.state.contentType === CourseContentType.HtmlContent;

    return (
      <LastPartProvider
        value={{
          clearLastPart: this.clearLastPart,
          lastPart: this.state.lastPart,
        }}
      >
        <SaveInProgress firstFetch={this.props.firstFetch} inProgress={this.props.inProgress} />
        <TopBar showToggleMenu={true} onToggleMenuClick={this.onToggleMenuClick} userName={this.props.userName} />
        <SideMenu
          expanded={this.state.menuExpanded}
          organizationName={organizationName}
          userName={userName}
          onClose={this.onToggleMenuClick}
          renderLinks={this.renderNavigation()}
          isOpen={true}
        />
        <SubTopbar withSidebar={true} isOpen={true}>
          {course.state.locked && <LockMessageWindow />}
          {hasLocale && <LocaleSelector currentLocale={this.state.currentLocale} setLocale={this.setLocale} />}
          {this.getPreviewComponent(location.search)}
        </SubTopbar>
        {this.props.courseInstId && (
          <CourseInstPreview
            search={location.search}
            isLinearProgressionEnabledInCourse={this.props.course.state.content.value.settings.linear}
          />
        )}
        {this.props.requestPreviewCourseState === RequestState.FAILED && (
          <CustomModal
            title="Course unavailable for preview"
            type={ModalType.Error}
            showModal={true}
            closeModal={this.props.resetPreview}
            message={displayPreviewCourseInstFailMessage}
          />
        )}
      </LastPartProvider>
    );
  }

  private onToggleMenuClick = () => {
    this.setState({ menuExpanded: !this.state.menuExpanded });
  };

  private previewCourseInst = () => {
    this.props.requestPreviewCourseInst(this.props.match.params.courseId);
  };

  private renderNavigation = () => {
    const { course, courseChanges } = this.props;

    const recentPublicationChanges = R.compose<CourseChanges, CourseChanges, CourseChanges, number[]>(
      R.map(change => change.versionId),
      R.takeWhile(change => change.patch.position.slice(0, 12) === "publications"),
      R.sort((a, b) => (a.versionId > b.versionId ? -1 : 1))
    )(courseChanges);

    const publicationVersion = pubVersion(course, recentPublicationChanges);

    return () => (
      <CourseNavigation
        course={this.props.course}
        currentLocale={this.state.currentLocale}
        displayedComponent={this.state.displayedComponent}
        editOperations={this.editOperations}
        publicationVersion={publicationVersion}
        previewCourseInst={this.previewCourseInst}
        navigationOperations={this.navigationOperations}
        rootUrl={`/course-author/${this.props.match.params.courseId}`}
        unitPath={this.state.unitPath}
        units={fullCourseViews.units(this.props.course) || []}
      />
    );
  };

  private getPreviewComponent(queryParams: string): JSX.Element {
    if (!positionQueryParameter(queryParams)) {
      const params = new URLSearchParams(this.props.location.search);
      return (
        <CourseTabs
          course={this.props.course}
          courseId={this.props.match.params.courseId}
          currentLocale={this.state.currentLocale}
          editOperations={this.editOperations}
          initialTab={params.get("tab") as CourseTabsEnum}
          migrateContentToHtml={this.props.migrateContentToHtml}
          navigationOperations={this.navigationOperations}
          resolve={this.props.resolve}
          resolvedData={this.props.resolvedData}
          unitPath={this.state.unitPath}
          userId={this.props.userId}
          localizedMessageApi={this.props.localizedMessageApi}
        />
      );
    }

    return (
      <SimplePreview
        course={this.props.course}
        courseId={this.props.match.params.courseId}
        currentLocale={this.state.currentLocale}
        displayedComponent={this.state.displayedComponent}
        displayedComponentUnitPath={this.state.unitPath}
        editOperations={this.editOperations}
        navigationOperations={this.navigationOperations}
        userId={this.props.userId}
      />
    );
  }

  private courseComponentListAt(path: CourseUnitView[]) {
    const destinationUnitView = R.last(path);
    if (destinationUnitView) {
      return courseUnitViews.components(destinationUnitView(this.props.course)) || [];
    } else {
      return fullCourseViews.units(this.props.course) || [];
    }
  }

  private setDisplayedComponentAndPath = (position: string) => {
    const { course } = this.props;
    const componentPath = findComponentPath(course, position) as CourseUnitView[];
    const componentView = R.last(componentPath) || null;
    const displayedComponent = componentView && componentView(course);

    const target = R.last(position.split("."));
    if (target === "test") {
      return this.setState({
        displayedComponent: fullCourseViews.assessment(course, componentPath as CourseUnitView[]),
        unitPath: componentPath,
      });
    }

    if (isUnit(displayedComponent)) {
      return this.setState({
        displayedComponent,
        unitPath: componentPath,
      });
    }

    const displayedComponentUnitPath = R.dropLast(1, componentPath) as CourseUnitView[];
    return this.setState({
      displayedComponent,
      unitPath: displayedComponentUnitPath,
    });
  };
}

function pubVersion(course: IFullCourse, recentPublicationChanges: number[]): number | undefined {
  const postPubVersion = R.find(versionIsPublished(course), recentPublicationChanges);
  return R.isNil(postPubVersion) ? postPubVersion : postPubVersion - 1;
}

function versionIsPublished(course: IFullCourse) {
  return (versionId: number) =>
    R.compose<ICoursePublication[], ICoursePublication[], number[], boolean>(
      R.any(vId => vId === versionId - 1),
      R.map((pub: ICoursePublication) => pub.revision),
      R.filter((pub: ICoursePublication) => !pub.deleted)
    )(fullCourseViews.publications(course) || []);
}
